import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, rupiah, formatApiError, STATUS_LABELS } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CityCombobox } from "@/components/CityCombobox";
import { wibToday } from "@/lib/date";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Trash2, Plus, Star, Megaphone, ArrowLeftRight, Share2, Package, MapPin, Camera, CheckCircle2, Pencil } from "lucide-react";
import { shareScheduleToWA, shareDriverToWA } from "@/lib/shareSchedule";
import { PhotoUpload } from "@/components/PhotoUpload";
import { DriverBadges } from "@/components/DriverBadges";
import { fileUrl } from "@/lib/api";

const PKG_LABELS = { paket_baru: "Paket Baru", dijemput: "Dijemput", dalam_pengiriman: "Dalam Pengiriman", terkirim: "Terkirim", dibatalkan: "Dibatalkan" };

const Stat = ({ label, value, accent }) => (
  <div className="border border-border bg-card p-4">
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-1 font-mono-terminal text-2xl font-bold ${accent ? "text-primary" : ""}`}>{value}</div>
  </div>
);

const OnboardStep = ({ n, done, title, desc, actionLabel, onAction, disabled, testId }) => (
  <div className="flex flex-col border border-border bg-card p-4" data-testid={testId}>
    <div className="flex items-center gap-2">
      {done ? (
        <CheckCircle2 className="h-6 w-6 text-emerald-400" data-testid={`${testId}-done`} />
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded-full border border-primary/60 font-mono-terminal text-xs font-bold text-primary">{n}</span>
      )}
      <span className={`font-semibold ${done ? "text-muted-foreground line-through" : ""}`}>{title}</span>
    </div>
    <p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground">{desc}</p>
    {!done && (
      <Button size="sm" className="mt-3 bg-primary font-bold text-primary-foreground hover:bg-primary/90" onClick={onAction} disabled={disabled} data-testid={`${testId}-action`}>
        {actionLabel}
      </Button>
    )}
  </div>
);

export default function DriverDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [orders, setOrders] = useState([]);
  const [bills, setBills] = useState([]);
  const [cities, setCities] = useState([]);
  const [vDialog, setVDialog] = useState(false);
  const [sDialog, setSDialog] = useState(false);
  const [ads, setAds] = useState([]);
  const [adPrices, setAdPrices] = useState({ daily: 25000, weekly: 150000, monthly: 500000 });
  const [adDialog, setAdDialog] = useState(false);
  const [adForm, setAdForm] = useState({ schedule_id: "", package: "weekly" });
  const [vForm, setVForm] = useState({ name: "", brand: "", model: "", year: "", plate: "", capacity: 7, facilities: [], photo: "" });
  const [sForm, setSForm] = useState({ service_type: "door_to_door", origin: "", destination: "", date: wibToday(), time: "", vehicle_name: "", price: "", capacity: 7, pickup_area: "", dropoff_area: "", duration: "", agree_commission: false, repeat: 1, waypoints: [] });
  const [sEditId, setSEditId] = useState(null);
  const [wp, setWp] = useState({ city: "", time: "" });
  const [pkgs, setPkgs] = useState([]);
  const [pkgAction, setPkgAction] = useState(null);
  const [pkgForm, setPkgForm] = useState({ city: "", note: "", photo: "" });
  const [tab, setTab] = useState("schedules");

  useEffect(() => {
    if (!loading && (!user || user.role !== "driver")) navigate("/masuk");
  }, [user, loading, navigate]);

  const load = () => {
    api.get("/driver/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/driver/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/driver/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/driver/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/driver/bills").then((r) => setBills(r.data)).catch(() => {});
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/driver/ads").then((r) => setAds(r.data)).catch(() => {});
    api.get("/driver/packages").then((r) => setPkgs(r.data)).catch(() => {});
    api.get("/settings").then((r) => setAdPrices(r.data.ad_prices)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "driver") load(); }, [user]);

  const openPkgAction = (p, type) => {
    setPkgAction({ pid: p.id, type, number: p.package_number });
    setPkgForm({ city: type === "pickup" ? p.origin : type === "deliver" ? p.destination : "", note: "", photo: "" });
  };
  const submitPkgAction = async () => {
    const { pid, type } = pkgAction;
    if ((type === "pickup" || type === "deliver") && !pkgForm.photo) { toast.error("Foto paket wajib diunggah"); return; }
    if (type === "checkpoint" && !pkgForm.city.trim()) { toast.error("Pilih kota lokasi"); return; }
    try {
      await api.post(`/driver/packages/${pid}/${type}`, pkgForm);
      toast.success(type === "pickup" ? "Paket ditandai dijemput" : type === "deliver" ? "Paket ditandai terkirim" : "Lokasi paket diperbarui");
      setPkgAction(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addVehicle = async () => {
    try {
      await api.post("/driver/vehicles", { ...vForm, capacity: Number(vForm.capacity), facilities: vForm.facilities });
      toast.success("Armada ditambahkan");
      setVDialog(false);
      setVForm({ name: "", brand: "", model: "", year: "", plate: "", capacity: 7, facilities: [], photo: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addSchedule = async () => {
    if (!sForm.origin || !sForm.destination) { toast.error("Pilih kota asal dan tujuan"); return; }
    if (sForm.origin === sForm.destination) { toast.error("Kota asal dan tujuan tidak boleh sama"); return; }
    if (!sForm.time) { toast.error("Isi jam keberangkatan"); return; }
    if (!sForm.vehicle_name) { toast.error("Pilih armada terlebih dahulu"); return; }
    if (!(Number(sForm.price) > 0)) { toast.error("Isi harga tiket lebih dari 0"); return; }
    if (!sForm.agree_commission) { toast.error("Setujui komisi platform 20% untuk menerbitkan"); return; }
    try {
      if (sEditId) {
        await api.put(`/driver/schedules/${sEditId}`, { ...sForm, price: Number(sForm.price), capacity: Number(sForm.capacity) });
        toast.success("Jadwal diperbarui");
      } else {
        const n = Math.max(1, Number(sForm.repeat) || 1);
        const base = new Date(sForm.date);
        for (let i = 0; i < n; i++) {
          const d = new Date(base); d.setDate(base.getDate() + i);
          await api.post("/driver/schedules", { ...sForm, date: d.toISOString().slice(0, 10), price: Number(sForm.price), capacity: Number(sForm.capacity) });
        }
        toast.success(n > 1 ? `${n} jadwal diterbitkan` : "Jadwal diterbitkan");
      }
      setSDialog(false); setSEditId(null);
      setSForm({ service_type: "door_to_door", origin: "", destination: "", date: wibToday(), time: "", vehicle_name: "", price: "", capacity: 7, pickup_area: "", dropoff_area: "", duration: "", agree_commission: false, repeat: 1, waypoints: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delVehicle = async (id) => { await api.delete(`/driver/vehicles/${id}`); load(); };
  const delSchedule = async (id) => { await api.delete(`/driver/schedules/${id}`); load(); };

  const copyLast = () => {
    if (!schedules.length) { toast.error("Belum ada jadwal sebelumnya"); return; }
    const s = schedules[0];
    setSForm((f) => ({ ...f, service_type: s.service_type, origin: s.origin, destination: s.destination, time: s.time, vehicle_name: s.vehicle_name, price: s.price, capacity: s.capacity, pickup_area: s.pickup_area || "" }));
    toast.success("Disalin dari jadwal terakhir");
  };

  const addWaypoint = () => {
    if (!wp.city) { toast.error("Pilih kota transit"); return; }
    if (sForm.waypoints.some((w) => w.city === wp.city)) { toast.error("Kota sudah ditambahkan"); return; }
    setSForm((f) => ({ ...f, waypoints: [...f.waypoints, { city: wp.city, time: wp.time }] }));
    setWp({ city: "", time: "" });
  };
  const removeWaypoint = (i) => setSForm((f) => ({ ...f, waypoints: f.waypoints.filter((_, idx) => idx !== i) }));

  const openScheduleDialog = () => {
    setSEditId(null);
    setSForm((f) => {
      const next = { ...f };
      if (!next.origin && user?.city) next.origin = user.city;
      if (!next.vehicle_name && vehicles.length === 1) { next.vehicle_name = vehicles[0].name; next.capacity = vehicles[0].capacity; }
      return next;
    });
    setSDialog(true);
  };

  const openEditSchedule = (s) => {
    setSEditId(s.id);
    setSForm({ service_type: s.service_type, origin: s.origin, destination: s.destination, date: s.date, time: s.time, vehicle_name: s.vehicle_name, price: s.price, capacity: s.capacity, pickup_area: s.pickup_area || "", dropoff_area: s.dropoff_area || "", duration: s.duration || "", agree_commission: true, repeat: 1, waypoints: s.waypoints || [] });
    setSDialog(true);
  };

  const buyAd = async () => {
    if (!adForm.schedule_id) { toast.error("Pilih jadwal untuk diiklankan"); return; }
    try {
      const { data } = await api.post("/driver/ads", adForm);
      toast.success("Iklan dibuat. Lanjutkan pembayaran via WhatsApp Admin.");
      window.open(data.wa_link, "_blank");
      setAdDialog(false);
      setAdForm({ schedule_id: "", package: "weekly" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading || !user) return <div className="min-h-screen bg-background" />;

  const stepVerified = !!user?.verified;
  const stepVehicle = vehicles.length > 0;
  const stepSchedule = schedules.length > 0;
  const onboardingDone = stepVerified && stepVehicle && stepSchedule;
  const stepsDone = [stepVerified, stepVehicle, stepSchedule].filter(Boolean).length;
  const goVehicle = () => { setTab("vehicles"); setVDialog(true); };
  const goSchedule = () => { setTab("schedules"); openScheduleDialog(); };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Dashboard Driver | Travele" noindex />
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{user.business_name || user.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {!user.verified && (
                <Badge className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/15"><ShieldAlert className="mr-1 h-3 w-3" /> Menunggu Verifikasi Admin</Badge>
              )}
              <DriverBadges verified={user.verified} priority={user.priority} rating={summary?.rating} reviews={summary?.reviews} trips={summary?.total_trips} testId="driver-profile-badges" />
            </div>
          </div>
          <Button
            onClick={() => shareDriverToWA({ business_name: user.business_name || user.name, city: user.city, avg_rating: summary?.rating, rating_count: summary?.reviews, total_trips: summary?.total_trips })}
            className="gap-2 bg-[#25D366] font-bold text-black hover:bg-[#25D366]/90"
            data-testid="driver-share-profile-btn"
          >
            <Share2 className="h-4 w-4" /> Bagikan Profil ke WhatsApp
          </Button>
        </div>

        {!user.verified && (
          <div className="mb-6 border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-300">
            Akun Anda belum diverifikasi Admin. Anda dapat menambah armada, namun jadwal baru dapat diterbitkan setelah verifikasi. Hubungi Admin Verifikasi WhatsApp 0811-9696-2828.
          </div>
        )}

        {!onboardingDone && (
          <div className="mb-8 border border-primary/40 bg-primary/5 p-5" data-testid="driver-onboarding">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold tracking-tight">Panduan Aktivasi Driver</h2>
              <span className="font-mono-terminal text-xs text-muted-foreground" data-testid="onboard-progress">{stepsDone}/3 langkah selesai</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Selesaikan langkah berikut agar jadwal Anda tampil di Digital Terminal dan bisa menerima order penumpang.</p>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <OnboardStep
                n={1}
                done={stepVerified}
                title="Verifikasi Admin"
                desc={stepVerified ? "Akun Anda sudah terverifikasi Admin." : "Hubungi Admin via WhatsApp untuk verifikasi akun mitra Anda."}
                actionLabel="Hubungi Admin"
                onAction={() => window.open("https://wa.me/6281196962828?text=" + encodeURIComponent(`Halo Admin Travele, mohon verifikasi akun mitra saya: ${user.business_name || user.name} (${user.phone}).`), "_blank", "noopener")}
                testId="onboard-verify"
              />
              <OnboardStep
                n={2}
                done={stepVehicle}
                title="Tambah Armada"
                desc={stepVehicle ? `${vehicles.length} armada terdaftar.` : "Daftarkan minimal 1 kendaraan beserta kapasitas & fasilitasnya."}
                actionLabel="Tambah Armada"
                onAction={goVehicle}
                testId="onboard-vehicle"
              />
              <OnboardStep
                n={3}
                done={stepSchedule}
                title="Terbitkan Jadwal"
                desc={stepSchedule ? `${schedules.length} jadwal terbit.` : (stepVerified && stepVehicle ? "Terbitkan jadwal keberangkatan agar muncul di terminal." : "Selesaikan verifikasi & tambah armada terlebih dahulu.")}
                actionLabel="Terbitkan Jadwal"
                onAction={goSchedule}
                disabled={!stepVerified || !stepVehicle}
                testId="onboard-schedule"
              />
            </div>
          </div>
        )}

        {summary && (
          <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat label="Order Hari Ini" value={summary.orders_today} />
            <Stat label="Jadwal Aktif" value={summary.active_schedules} />
            <Stat label="Kursi Terjual" value={summary.seats_sold} />
            <Stat label="Pendapatan Kotor" value={rupiah(summary.gross_income)} accent />
            <Stat label="Komisi Platform" value={rupiah(summary.commission)} />
            <Stat label="Tagihan Belum Bayar" value={rupiah(summary.unpaid_bills)} />
            <Stat label="Rating" value={`${summary.rating} ★`} accent />
            <Stat label="Total Perjalanan" value={summary.total_trips} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="schedules" data-testid="tab-schedules">Jadwal</TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles">Armada</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Order</TabsTrigger>
            <TabsTrigger value="bills" data-testid="tab-bills">Komisi & Tagihan</TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads">Iklan</TabsTrigger>
            <TabsTrigger value="packages" data-testid="tab-driver-packages">Paket Kilat</TabsTrigger>
          </TabsList>

          {/* Schedules */}
          <TabsContent value="schedules">
            <div className="mb-3 flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 p-3 text-xs" data-testid="commission-policy-schedules">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-amber-200/90">Komisi wajib dibayar maksimal <b>3×24 jam</b> setelah keberangkatan dilalui. Jika telat, pemasangan jadwal baru akan ditutup hingga komisi lunas.</p>
            </div>
            <div className="mb-3 flex justify-end">
              <Button data-testid="add-schedule-btn" onClick={openScheduleDialog} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1 h-4 w-4" /> Terbitkan Jadwal
              </Button>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rute</TableHead><TableHead>Tanggal / Jam</TableHead><TableHead>Armada</TableHead>
                    <TableHead>Harga</TableHead><TableHead>Kursi</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedules.map((s) => (
                    <TableRow key={s.id} data-testid={`driver-schedule-${s.id}`}>
                      <TableCell className="font-medium">{s.origin} → {s.destination}</TableCell>
                      <TableCell className="font-mono-terminal">{s.date} {s.time}</TableCell>
                      <TableCell>{s.vehicle_name}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(s.price)}</TableCell>
                      <TableCell>{s.seats_left}/{s.capacity}</TableCell>
                      <TableCell><Badge variant="secondary">{s.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" data-testid={`driver-share-btn-${s.id}`} title="Bagikan jadwal ke WhatsApp" onClick={() => shareScheduleToWA(s, user?.business_name || user?.name)}>
                            <Share2 className="h-4 w-4 text-[#25D366]" />
                          </Button>
                          <Button size="icon" variant="ghost" data-testid={`driver-edit-btn-${s.id}`} title="Edit jadwal" onClick={() => openEditSchedule(s)}>
                            <Pencil className="h-4 w-4 text-primary" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => delSchedule(s.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {schedules.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Belum ada jadwal.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Vehicles */}
          <TabsContent value="vehicles">
            <div className="mb-3 flex justify-end">
              <Button data-testid="add-vehicle-btn" onClick={() => setVDialog(true)} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1 h-4 w-4" /> Tambah Armada
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {vehicles.map((v) => (
                <div key={v.id} className="overflow-hidden border border-border bg-card" data-testid={`vehicle-${v.id}`}>
                  {v.photo ? (
                    <img src={fileUrl(v.photo)} alt={v.name} className="h-36 w-full object-cover" data-testid={`vehicle-photo-${v.id}`} />
                  ) : (
                    <div className="flex h-36 w-full items-center justify-center bg-muted/30 text-xs text-muted-foreground">Belum ada foto</div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold">{v.name}</div>
                        <div className="text-sm text-muted-foreground">{v.brand} {v.model} · {v.capacity} kursi</div>
                        <div className="text-xs text-muted-foreground">{v.plate}</div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => delVehicle(v.id)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(v.facilities || []).map((f) => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}
                    </div>
                  </div>
                </div>
              ))}
              {vehicles.length === 0 && <p className="text-muted-foreground">Belum ada armada.</p>}
            </div>
          </TabsContent>

          {/* Orders */}
          <TabsContent value="orders">
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>No. Order</TableHead><TableHead>Penumpang</TableHead><TableHead>Rute</TableHead>
                  <TableHead>Pax</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {orders.map((o) => (
                    <TableRow key={o.id} data-testid={`driver-order-${o.order_number}`}>
                      <TableCell className="font-mono-terminal">{o.order_number}</TableCell>
                      <TableCell>{o.passenger_name}</TableCell>
                      <TableCell>{o.origin} → {o.destination}</TableCell>
                      <TableCell>{o.pax}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(o.total)}</TableCell>
                      <TableCell><Badge variant="secondary">{STATUS_LABELS[o.status] || o.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {orders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Belum ada order.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Bills */}
          <TabsContent value="bills">
            <div className="mb-3 flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="commission-policy-bills">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-amber-200/90">
                <b className="text-amber-300">Ketentuan Pembayaran Komisi:</b> Komisi wajib dibayarkan maksimal <b>3×24 jam</b> setelah jadwal keberangkatan dilalui. Bila melebihi batas waktu, driver <b>tidak dapat memasang jadwal keberangkatan baru</b> hingga komisi dilunasi.
              </p>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>No. Order</TableHead><TableHead>Nilai Transaksi</TableHead><TableHead>Komisi (20%)</TableHead>
                  <TableHead>Jatuh Tempo</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bills.map((b) => (
                    <TableRow key={b.id} data-testid={`driver-bill-${b.id}`}>
                      <TableCell className="font-mono-terminal">{b.order_number}</TableCell>
                      <TableCell className="font-mono-terminal">{rupiah(b.transaction_value)}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(b.amount)}</TableCell>
                      <TableCell>{b.due_date}</TableCell>
                      <TableCell><Badge variant={b.status === "lunas" ? "default" : "secondary"}>{b.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {bills.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Belum ada tagihan.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="ads">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">Beli slot <b>Sponsor</b> agar jadwal Anda tampil di posisi teratas (maks 3 per rute).</p>
              <Button data-testid="buy-ad-btn" onClick={() => setAdDialog(true)} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                <Megaphone className="mr-1 h-4 w-4" /> Beli Iklan Sponsor
              </Button>
            </div>
            <div className="border border-border">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Rute</TableHead><TableHead>Armada</TableHead><TableHead>Paket</TableHead>
                  <TableHead>Harga</TableHead><TableHead>Periode</TableHead><TableHead>Tayangan/Klik</TableHead><TableHead>Order (Konversi)</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {ads.map((a) => (
                    <TableRow key={a.id} data-testid={`driver-ad-${a.id}`}>
                      <TableCell>{a.origin} → {a.destination}</TableCell>
                      <TableCell>{a.vehicle_name}</TableCell>
                      <TableCell className="capitalize">{a.package}</TableCell>
                      <TableCell className="font-mono-terminal text-primary">{rupiah(a.price)}</TableCell>
                      <TableCell className="font-mono-terminal text-xs">{a.start_date} – {a.end_date}</TableCell>
                      <TableCell className="font-mono-terminal">{a.impressions}/{a.clicks}</TableCell>
                      <TableCell className="font-mono-terminal">{a.orders || 0}{a.clicks > 0 ? ` (${Math.round((a.orders / a.clicks) * 100)}%)` : ""}</TableCell>
                      <TableCell><Badge variant={a.status === "aktif" ? "default" : "secondary"} className={a.status === "aktif" ? "bg-primary text-primary-foreground" : ""}>{a.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {ads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Belum ada iklan.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="packages">
            <p className="mb-3 text-sm text-muted-foreground">Paket yang ditugaskan Admin kepada Anda. <b className="text-foreground">Wajib unggah foto</b> saat menjemput paket dari pengirim dan saat paket diterima customer, serta perbarui lokasi kota selama perjalanan.</p>
            <div className="grid gap-3">
              {pkgs.length === 0 && <p className="border border-border bg-card p-6 text-center text-muted-foreground" data-testid="driver-no-packages">Belum ada paket yang ditugaskan.</p>}
              {pkgs.map((p) => (
                <div key={p.id} data-testid={`driver-pkg-${p.package_number}`} className="border border-border bg-card p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono-terminal font-bold text-primary">{p.package_number}</span>
                    <Badge variant="secondary">{PKG_LABELS[p.status] || p.status}</Badge>
                  </div>
                  <div className="mt-2 grid gap-0.5 text-sm">
                    <div>{p.origin} → {p.destination} · {p.item_type === "dokumen" ? "Dokumen" : "Barang"}</div>
                    <div className="text-muted-foreground">Pengirim: {p.sender_name} ({p.sender_phone}) · Penerima: {p.receiver_name} ({p.receiver_phone})</div>
                    {p.dropoff_address && <div className="text-xs text-muted-foreground">Alamat tujuan: {p.dropoff_address}</div>}
                  </div>
                  {(p.checkpoints || []).length > 0 && (
                    <ol className="mt-3 space-y-2 border-t border-border pt-3">
                      {p.checkpoints.map((c, i) => (
                        <li key={i} className="flex gap-2 text-sm">
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <div className="flex-1">
                            <div className="font-medium">{c.city} <span className="text-xs font-normal text-muted-foreground">· {PKG_LABELS[c.status] || c.status}</span></div>
                            {c.note && <div className="text-xs text-muted-foreground">{c.note}</div>}
                            <div className="font-mono-terminal text-[10px] text-muted-foreground">{new Date(c.at).toLocaleString("id-ID")}</div>
                            {c.photo && <img src={fileUrl(c.photo)} alt="Foto paket" loading="lazy" className="mt-1 h-24 rounded border border-border object-cover" />}
                          </div>
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {p.status === "paket_baru" && <Button size="sm" data-testid={`pkg-pickup-${p.package_number}`} onClick={() => openPkgAction(p, "pickup")} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90"><Camera className="mr-1 h-4 w-4" /> Konfirmasi Jemput</Button>}
                    {["dijemput", "dalam_pengiriman"].includes(p.status) && <Button size="sm" variant="secondary" data-testid={`pkg-checkpoint-${p.package_number}`} onClick={() => openPkgAction(p, "checkpoint")}><MapPin className="mr-1 h-4 w-4" /> Tambah Lokasi</Button>}
                    {["dijemput", "dalam_pengiriman"].includes(p.status) && <Button size="sm" data-testid={`pkg-deliver-${p.package_number}`} onClick={() => openPkgAction(p, "deliver")} className="bg-emerald-500 font-bold text-white hover:bg-emerald-600"><CheckCircle2 className="mr-1 h-4 w-4" /> Konfirmasi Terkirim</Button>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Vehicle Dialog */}
      <Dialog open={vDialog} onOpenChange={setVDialog}>
        <DialogContent data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>Tambah Armada</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Nama Armada</Label><Input data-testid="v-name" value={vForm.name} onChange={(e) => setVForm({ ...vForm, name: e.target.value })} placeholder="cth. Hiace Premio" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Merek</Label><Input value={vForm.brand} onChange={(e) => setVForm({ ...vForm, brand: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Kapasitas</Label><Input type="number" value={vForm.capacity} onChange={(e) => setVForm({ ...vForm, capacity: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Tahun</Label><Input value={vForm.year} onChange={(e) => setVForm({ ...vForm, year: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>No. Polisi</Label><Input value={vForm.plate} onChange={(e) => setVForm({ ...vForm, plate: e.target.value })} /></div>
            </div>
            <div className="grid gap-1.5">
              <Label>Fasilitas</Label>
              <div className="flex flex-wrap gap-1.5">
                {["AC", "WiFi", "Charger", "Reclining Seat", "Air Mineral", "Service Makan"].map((f) => {
                  const on = vForm.facilities.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      data-testid={`v-facility-${f.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setVForm({ ...vForm, facilities: on ? vForm.facilities.filter((x) => x !== f) : [...vForm.facilities, f] })}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                    >
                      {f}
                    </button>
                  );
                })}
              </div>
              <Input value={vForm.facilities.join(", ")} onChange={(e) => setVForm({ ...vForm, facilities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="AC, WiFi, Charger, Service Makan" />
              <span className="text-[11px] text-muted-foreground">Klik chip untuk memilih cepat, atau ketik manual dipisah koma.</span>
            </div>
            <PhotoUpload label="Foto Armada" testId="vehicle-photo-upload" value={vForm.photo} onChange={(url) => setVForm({ ...vForm, photo: url })} />
          </div>
          <DialogFooter><Button data-testid="v-submit" onClick={addVehicle} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={sDialog} onOpenChange={(o) => { setSDialog(o); if (!o) setSEditId(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="schedule-dialog">
          <DialogHeader><DialogTitle>{sEditId ? "Edit Jadwal" : "Terbitkan Jadwal"}</DialogTitle></DialogHeader>
          <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs" data-testid="commission-policy-dialog">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <p className="text-amber-200/90">Pastikan komisi perjalanan sebelumnya lunas. Komisi wajib dibayar maksimal <b>3×24 jam</b> setelah keberangkatan; keterlambatan dapat menutup pemasangan jadwal.</p>
          </div>
          <Button type="button" variant="secondary" size="sm" data-testid="copy-last-btn" onClick={copyLast} className="w-fit">Salin dari jadwal terakhir</Button>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Layanan</Label>
              <Select value={sForm.service_type} onValueChange={(v) => setSForm({ ...sForm, service_type: v })}>
                <SelectTrigger data-testid="s-service"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="door_to_door">Travel Door to Door</SelectItem><SelectItem value="charter">Charter Drop Off</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Asal</Label>
                <CityCombobox cities={cities} value={sForm.origin} onChange={(v) => setSForm({ ...sForm, origin: v })} placeholder="Cari kota asal" testId="s-origin" />
              </div>
              <div className="grid gap-1.5"><Label>Tujuan</Label>
                <CityCombobox cities={cities} value={sForm.destination} onChange={(v) => setSForm({ ...sForm, destination: v })} placeholder="Cari kota tujuan" testId="s-destination" />
              </div>
            </div>
            <button type="button" data-testid="s-swap" onClick={() => setSForm((f) => ({ ...f, origin: f.destination, destination: f.origin }))} className="flex w-fit items-center gap-1 text-xs text-primary hover:underline">
              <ArrowLeftRight className="h-3 w-3" /> Tukar asal &amp; tujuan
            </button>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Tanggal</Label><Input data-testid="s-date" type="date" value={sForm.date} onChange={(e) => setSForm({ ...sForm, date: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Jam</Label><Input data-testid="s-time" type="time" value={sForm.time} onChange={(e) => setSForm({ ...sForm, time: e.target.value })} /></div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">Jam cepat:</span>
              {["05:00", "06:00", "07:00", "08:00", "10:00", "13:00", "16:00", "19:00", "21:00"].map((t) => (
                <button key={t} type="button" data-testid={`s-time-preset-${t}`} onClick={() => setSForm((f) => ({ ...f, time: t }))} className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${sForm.time === t ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>{t}</button>
              ))}
            </div>
            <div className="grid gap-1.5"><Label>Armada</Label>
              {vehicles.length === 0 ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-primary/50 bg-primary/5 p-3 text-xs text-muted-foreground" data-testid="s-no-vehicle">
                  <span>Belum ada armada. Tambahkan armada dulu untuk menerbitkan jadwal.</span>
                  <Button type="button" size="sm" variant="secondary" data-testid="s-add-vehicle" onClick={() => { setSDialog(false); setVDialog(true); }}>Tambah Armada</Button>
                </div>
              ) : (
                <Select value={sForm.vehicle_name} onValueChange={(v) => { const veh = vehicles.find((x) => x.name === v); setSForm({ ...sForm, vehicle_name: v, capacity: veh ? veh.capacity : sForm.capacity }); }}>
                  <SelectTrigger data-testid="s-vehicle"><SelectValue placeholder="Pilih armada" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Harga Tiket</Label><Input data-testid="s-price" type="number" value={sForm.price} onChange={(e) => setSForm({ ...sForm, price: e.target.value })} /><span className="text-xs text-primary">{rupiah(Number(sForm.price) || 0)}</span></div>
              <div className="grid gap-1.5"><Label>Kapasitas Kursi</Label><Input data-testid="s-capacity" type="number" value={sForm.capacity} onChange={(e) => setSForm({ ...sForm, capacity: e.target.value })} /></div>
            </div>
            <div className="grid gap-1.5"><Label>Area Penjemputan</Label><Input value={sForm.pickup_area} onChange={(e) => setSForm({ ...sForm, pickup_area: e.target.value })} /></div>
            <div className="grid gap-1.5" data-testid="waypoints-field">
              <Label>Kota yang Dilalui + Perkiraan Jam (opsional)</Label>
              <p className="text-[11px] text-muted-foreground">Tambahkan kota transit agar penumpang di sepanjang rute bisa memesan (order dadakan). Contoh: Kendal (07:00) · Pekalongan (08:30) · Cirebon (10:00).</p>
              {sForm.waypoints.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {sForm.waypoints.map((w, i) => (
                    <span key={i} data-testid={`wp-chip-${i}`} className="flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs text-primary">
                      {w.city}{w.time ? ` (${w.time})` : ""}
                      <button type="button" onClick={() => removeWaypoint(i)} className="ml-1 text-muted-foreground hover:text-red-400">×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <div className="flex-1"><CityCombobox cities={cities.filter((c) => c.name !== sForm.origin && c.name !== sForm.destination)} value={wp.city} onChange={(v) => setWp({ ...wp, city: v })} placeholder="Cari kota transit" testId="wp-city" /></div>
                <Input data-testid="wp-time" type="time" value={wp.time} onChange={(e) => setWp({ ...wp, time: e.target.value })} className="w-32" />
                <Button type="button" variant="secondary" data-testid="wp-add" onClick={addWaypoint}>Tambah</Button>
              </div>
            </div>
            {!sEditId && (
            <div className="grid gap-1.5"><Label>Terbitkan untuk</Label>
              <Select value={String(sForm.repeat)} onValueChange={(v) => setSForm({ ...sForm, repeat: Number(v) })}>
                <SelectTrigger data-testid="s-repeat"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">1 hari</SelectItem><SelectItem value="3">3 hari berturut-turut</SelectItem><SelectItem value="7">7 hari berturut-turut</SelectItem></SelectContent>
              </Select>
            </div>
            )}
            <div className="flex items-start gap-2 border border-primary/40 bg-primary/5 p-3 text-xs">
              <Checkbox data-testid="s-agree" checked={sForm.agree_commission} onCheckedChange={(v) => setSForm({ ...sForm, agree_commission: !!v })} className="mt-0.5" />
              <span className="text-muted-foreground">Dengan menerbitkan jadwal ini, saya menyetujui komisi platform Travele sebesar 20% dari nilai tiket yang saya tetapkan untuk setiap order dari platform.</span>
            </div>
          </div>
          <DialogFooter><Button data-testid="s-submit" onClick={addSchedule} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">{sEditId ? "Simpan Perubahan" : "Terbitkan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ad Dialog */}
      <Dialog open={adDialog} onOpenChange={setAdDialog}>
        <DialogContent data-testid="ad-dialog">
          <DialogHeader><DialogTitle>Beli Iklan Sponsor</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Jadwal yang diiklankan akan ditandai <b className="text-primary">Sponsor</b> dan tampil di posisi teratas hasil pencarian rute (maksimal 3 driver per rute).</p>
          <div className="mt-2 grid gap-3">
            <div className="grid gap-1.5">
              <Label>Pilih Jadwal</Label>
              <Select value={adForm.schedule_id} onValueChange={(v) => setAdForm({ ...adForm, schedule_id: v })}>
                <SelectTrigger data-testid="ad-schedule"><SelectValue placeholder="Pilih jadwal" /></SelectTrigger>
                <SelectContent>
                  {schedules.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.origin} → {s.destination} · {s.date} {s.time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Paket Iklan</Label>
              <Select value={adForm.package} onValueChange={(v) => setAdForm({ ...adForm, package: v })}>
                <SelectTrigger data-testid="ad-package"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Harian — {rupiah(adPrices.daily)}</SelectItem>
                  <SelectItem value="weekly">Mingguan — {rupiah(adPrices.weekly)}</SelectItem>
                  <SelectItem value="monthly">Bulanan — {rupiah(adPrices.monthly)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-3">
              <span className="text-muted-foreground">Total</span>
              <span className="font-mono-terminal text-xl font-bold text-primary">{rupiah(adPrices[adForm.package])}</span>
            </div>
          </div>
          <DialogFooter><Button data-testid="ad-submit" onClick={buyAd} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Lanjut Bayar via WhatsApp Admin</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pkgAction} onOpenChange={(o) => !o && setPkgAction(null)}>
        <DialogContent data-testid="pkg-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {pkgAction?.type === "pickup" ? "Konfirmasi Jemput Paket" : pkgAction?.type === "deliver" ? "Konfirmasi Paket Terkirim" : "Tambah Lokasi Paket"}
              {pkgAction?.number ? ` · ${pkgAction.number}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Kota / Lokasi Saat Ini</Label>
              <Select value={pkgForm.city} onValueChange={(v) => setPkgForm({ ...pkgForm, city: v })}>
                <SelectTrigger data-testid="pkg-city"><SelectValue placeholder="Pilih kota" /></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Catatan (opsional)</Label><Input data-testid="pkg-note" value={pkgForm.note} onChange={(e) => setPkgForm({ ...pkgForm, note: e.target.value })} placeholder="cth. transit di terminal, estimasi tiba sore" /></div>
            <PhotoUpload
              label={pkgAction?.type === "pickup" ? "Foto Paket saat Dijemput (wajib)" : pkgAction?.type === "deliver" ? "Foto Paket saat Diterima Customer (wajib)" : "Foto (opsional)"}
              testId="pkg-photo-upload"
              value={pkgForm.photo}
              onChange={(url) => setPkgForm({ ...pkgForm, photo: url })}
            />
          </div>
          <DialogFooter><Button data-testid="pkg-action-submit" onClick={submitPkgAction} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
